import { useState, useEffect, useRef } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { ChevronDown, Menu, X } from "lucide-react";

const solutions = [
  { label: "Restricted List", href: "/restricted-list", desc: "Stop insider trading before it’s sent." },
  { label: "Active Listening™", href: "/active-listening", desc: "Catch risk before it’s sent." },
  { label: "Rapid Deploy™", href: "/rapid-deploy", desc: "Uncover risk at scale." }
];

const trustSecurity = [
  { label: "Security", href: "/trust-security", desc: "Enterprise-grade infrastructure & controls" },
  { label: "Privacy", href: "/trust-privacy", desc: "Data protection & confidentiality" },
  { label: "Responsible AI", href: "/trust-responsible-ai", desc: "Ethical AI principles & governance" }
];

const resources = [
  { label: "Case Studies", href: "/case-studies", desc: "Real-world customer success stories" },
  { label: "FAQs", href: "/faqs", desc: "Frequently asked questions" },
  { label: "Support", href: "/support", desc: "Contact our team & self-serve docs" },
  { label: "Blog", href: "/blog", desc: "Industry insights & updates" }
];

export function Navbar({ solid = false, logoOnly = false }: { solid?: boolean; logoOnly?: boolean } = {}) {
  const [navVisible, setNavVisible] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const previousScrollY = useRef(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [trustOpen, setTrustOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(true);
  const [mobileTrustOpen, setMobileTrustOpen] = useState(false);
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false);
  useEffect(() => {
    if (typeof document === "undefined") return;
    const prev = document.body.style.overflow;
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  useEffect(() => {
    if (!logoOnly || typeof window === "undefined") return;

    previousScrollY.current = window.scrollY;
    setIsScrolled(window.scrollY > 16);

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollDifference = currentScrollY - previousScrollY.current;
      setIsScrolled(currentScrollY > 16);

      if (currentScrollY <= 16) {
        setNavVisible(true);
      } else if (scrollDifference > 4) {
        setNavVisible(false);
      } else if (scrollDifference < -4) {
        setNavVisible(true);
      }

      previousScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [logoOnly]);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 will-change-transform transition-[transform,background-color,border-color,backdrop-filter] duration-500 ease-in-out motion-reduce:transition-none ${
      logoOnly
        ? `${isScrolled ? "border-b border-border/40 bg-background/50 backdrop-blur-md" : "border-transparent bg-transparent"} ${navVisible ? (isScrolled ? "nav-slide-down" : "translate-y-0") : "nav-slide-up"}`
        : `border-b border-border/40 ${solid ? "bg-black" : "bg-background/50 backdrop-blur-md"}`
    }`}>
      <div className="container mx-auto px-6 h-20 flex items-center justify-between">
        {logoOnly ? (
          <div className="flex items-center shrink-0">
            <img src={`${import.meta.env.BASE_URL}harmcheck-logo-360.webp`} alt="HarmCheck" width="180" height="36" className="h-9 w-auto" />
          </div>
        ) : (
          <Link href="/" className="flex items-center cursor-pointer shrink-0" data-testid="link-home">
            <img src={`${import.meta.env.BASE_URL}harmcheck-logo-360.webp`} alt="HarmCheck logo — go to homepage" width="180" height="36" className="h-9 w-auto" />
          </Link>
        )}

        {!logoOnly && <div className="hidden lg:flex items-center gap-6 text-sm font-medium text-white/80 ml-8">
          <div
            className="relative"
            onMouseEnter={() => setDropdownOpen(true)}
            onMouseLeave={() => setDropdownOpen(false)}
          >
            <button className="flex items-center gap-1 hover:text-foreground transition-colors">
              Solutions
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
            </button>
            {dropdownOpen && (
              <div className="absolute top-full left-0 pt-2 w-72 z-50">
              <div className="border border-border/40 bg-black shadow-xl">
                {solutions.map((item) => (
                  <Link
                    key={item.label}
                    href={item.href}
                    className="block px-4 py-3 hover:bg-white/5 transition-colors border-b border-border/20 last:border-b-0"
                    onClick={() => setDropdownOpen(false)}
                  >
                    <div className="font-medium text-white">{item.label}</div>
                    <div className="text-sm text-muted-foreground mt-0.5">{item.desc}</div>
                  </Link>
                ))}
              </div>
              </div>
            )}
          </div>

          <Link href="/why-harmcheck" className="hover:text-foreground transition-colors" data-testid="link-why">Why HarmCheck</Link>

          <Link href="/harmcheck-vs-llms" className="hover:text-foreground transition-colors" data-testid="link-vs-llms">HarmCheck vs LLMs</Link>

          <div
            className="relative"
            onMouseEnter={() => setTrustOpen(true)}
            onMouseLeave={() => setTrustOpen(false)}
          >
            <button className="flex items-center gap-1 hover:text-foreground transition-colors" data-testid="link-trust">
              Trust & Security
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${trustOpen ? "rotate-180" : ""}`} />
            </button>
            {trustOpen && (
              <div className="absolute top-full left-0 pt-2 w-64 z-50">
              <div className="border border-border/40 bg-black shadow-xl">
                {trustSecurity.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    className="block px-4 py-3 hover:bg-white/5 transition-colors border-b border-border/20 last:border-b-0"
                    onClick={() => setTrustOpen(false)}
                  >
                    <div className="font-medium text-white">{item.label}</div>
                    <div className="text-sm text-muted-foreground mt-0.5">{item.desc}</div>
                  </a>
                ))}
              </div>
              </div>
            )}
          </div>

          <div
            className="relative"
            onMouseEnter={() => setResourcesOpen(true)}
            onMouseLeave={() => setResourcesOpen(false)}
          >
            <button className="flex items-center gap-1 hover:text-foreground transition-colors">
              Resources
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${resourcesOpen ? "rotate-180" : ""}`} />
            </button>
            {resourcesOpen && (
              <div className="absolute top-full left-0 pt-2 w-64 z-50">
              <div className="border border-border/40 bg-black shadow-xl">
                {resources.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    className="block px-4 py-3 hover:bg-white/5 transition-colors border-b border-border/20 last:border-b-0"
                    onClick={() => setResourcesOpen(false)}
                  >
                    <div className="font-medium text-white">{item.label}</div>
                    <div className="text-sm text-muted-foreground mt-0.5">{item.desc}</div>
                  </a>
                ))}
              </div>
              </div>
            )}
          </div>

          <a href="/about" className="hover:text-foreground transition-colors">About</a>
        </div>}

        {!logoOnly && <div className="hidden lg:flex items-center gap-4 shrink-0">
          <Button className="bg-primary hover:bg-primary/90 text-white rounded-none px-6 border-none" asChild>
            <a href="/book-a-demo">Request a Demo</a>
          </Button>
        </div>}

        {!logoOnly && <button
          className="lg:hidden flex items-center justify-center w-10 h-10 text-white/80 hover:text-white transition-colors"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>}
      </div>

      {!logoOnly && mobileOpen && (
        <div className="lg:hidden border-t border-border/40 bg-background/95 backdrop-blur-md h-[calc(100dvh-5rem)] flex flex-col">
          <div className="container mx-auto px-6 py-4 flex flex-col gap-1 flex-1 overflow-y-auto overscroll-contain">
            <button
              className="flex items-center justify-between w-full py-2.5 text-lg font-medium text-white hover:text-white transition-colors"
              onClick={() => setMobileSolutionsOpen(!mobileSolutionsOpen)}
            >
              Solutions
              <ChevronDown className={`w-4 h-4 transition-transform ${mobileSolutionsOpen ? "rotate-180" : ""}`} />
            </button>
            {mobileSolutionsOpen && (
              <div className="pl-4 flex flex-col gap-0.5 mb-1">
                {solutions.map((item) => (
                  <Link
                    key={item.label}
                    href={item.href}
                    className="block py-1.5 text-base text-white hover:text-white transition-colors"
                    onClick={() => setMobileOpen(false)}
                  >
                    <div className="font-medium">{item.label}</div>
                    <div className="text-sm text-muted-foreground">{item.desc}</div>
                  </Link>
                ))}
              </div>
            )}
            <Link
              href="/why-harmcheck"
              className="py-2.5 text-lg font-medium text-white hover:text-white transition-colors"
              onClick={() => setMobileOpen(false)}
            >
              Why HarmCheck
            </Link>
            <Link
              href="/harmcheck-vs-llms"
              className="py-2.5 text-lg font-medium text-white hover:text-white transition-colors"
              onClick={() => setMobileOpen(false)}
            >
              HarmCheck vs LLMs
            </Link>
            <button
              className="flex items-center justify-between w-full py-2.5 text-lg font-medium text-white hover:text-white transition-colors"
              onClick={() => setMobileTrustOpen(!mobileTrustOpen)}
            >
              Trust & Security
              <ChevronDown className={`w-4 h-4 transition-transform ${mobileTrustOpen ? "rotate-180" : ""}`} />
            </button>
            {mobileTrustOpen && (
              <div className="pl-4 flex flex-col gap-0.5 mb-1">
                {trustSecurity.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    className="block py-1.5 text-base text-white hover:text-white transition-colors"
                    onClick={() => { setMobileOpen(false); setMobileTrustOpen(false); }}
                  >
                    <div className="font-medium">{item.label}</div>
                    <div className="text-sm text-muted-foreground">{item.desc}</div>
                  </a>
                ))}
              </div>
            )}
            <button
              className="flex items-center justify-between w-full py-2.5 text-lg font-medium text-white hover:text-white transition-colors"
              onClick={() => setMobileResourcesOpen(!mobileResourcesOpen)}
            >
              Resources
              <ChevronDown className={`w-4 h-4 transition-transform ${mobileResourcesOpen ? "rotate-180" : ""}`} />
            </button>
            {mobileResourcesOpen && (
              <div className="pl-4 flex flex-col gap-0.5 mb-1">
                {resources.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    className="block py-1.5 text-base text-white hover:text-white transition-colors"
                    onClick={() => { setMobileOpen(false); setMobileResourcesOpen(false); }}
                  >
                    <div className="font-medium">{item.label}</div>
                    <div className="text-sm text-muted-foreground">{item.desc}</div>
                  </a>
                ))}
              </div>
            )}
            <a
              href="/about"
              className="py-2.5 text-lg font-medium text-white hover:text-white transition-colors"
              onClick={() => setMobileOpen(false)}
            >
              About
            </a>
          </div>
          <div className="container mx-auto px-6 py-4 border-t border-border/40 bg-background/95 shrink-0">
            <Button size="lg" className="h-14 px-8 text-base bg-primary hover:bg-primary/90 text-white rounded-none border-none w-full" asChild>
              <a href="/book-a-demo" onClick={() => setMobileOpen(false)}>Request a Demo</a>
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
}
